import { useEffect, useState } from 'react'
import {
  ChartPieIcon,
  DocumentReportIcon,
  HomeIcon,
  XIcon,
} from '@heroicons/react/solid'
import { ScaleIcon } from '@heroicons/react/outline'
import useRouterHistory from '@hooks/useRouterHistory'
import cx from 'classnames'
import Link from 'next/link'

interface SidebarProps {
  isOpen?: boolean
  onClose: () => void
  className?: string
}

export function Sidebar({ isOpen, onClose, className }: SidebarProps) {
  const [isMobile, setIsMobile] = useState(false)
  const { history } = useRouterHistory()

  // Detect if screen is mobile (<1280px)
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1280)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    onClose()
  }, [history])

  const navItems = [
    {
      href: '/dao',
      icon: <HomeIcon className="w-5 h-5 mr-1" />,
      label: 'Home',
    },
    {
      href: '/dao/proposals',
      icon: <DocumentReportIcon className="w-5 h-5 mr-1" />,
      label: 'Proposals',
    },
    {
      href: '/dao/neutrals',
      icon: <ScaleIcon className="w-5 h-5 mr-1" />,
      label: 'Neutrals',
    },
    {
      href: '/dao/executors',
      icon: <ChartPieIcon className="w-5 h-5 mr-1" />,
      label: 'Executors',
    },
  ]

  const sidebarContent = (
    <div className={cx('grid gap-4 sm:gap-6 p-5', className)}>
      {isMobile && (
        <div className="flex justify-end">
          <div className="cursor-pointer" onClick={onClose}>
            <XIcon className="w-5 h-5 text-white" />
          </div>
        </div>
      )}
      {navItems.map(({ href, icon, label }, index) => (
        <Link key={index} href={href}>
          <div className="flex cursor-pointer gap-2 items-center min-w-[36px]">
            {icon}
            <span className="text-xs">{label}</span>
          </div>
        </Link>
      ))}
    </div>
  )

  if (!isMobile) {
    return <div className={cx('bg-bkg-2', className)}>{sidebarContent}</div>
  }

  return (
    <div
      className={cx(
        'fixed inset-0 z-50 xl:hidden flex',
        isOpen ? 'pointer-events-auto' : 'pointer-events-none',
      )}
    >
      <button
        className={cx(
          'absolute inset-0 bg-black/50 backdrop-blur transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0',
        )}
        onClick={onClose}
      />

      <button
        className={cx(
          'relative grid items-start w-64 h-full bg-bkg-2 p-6 shadow-xl transition-transform duration-300',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {sidebarContent}
      </button>
    </div>
  )
}
